import React, { useState } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

const dateInput = value => (value ? String(value).slice(0, 10) : '');
const numberText = value => (value === undefined || value === null || value === '' ? '' : String(value));

// Edits one outlet pack (stock, selling price, MRP, MFG and expiry) like the inventory editor.
export default function OutletStockEditor({ item, token, onSaved, onInventoryEdit }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  if (item.catalogSource === 'ProductsVdMart') {
    return <button type="button" className="text-blue-700 underline" onClick={() => onInventoryEdit(item)}>Edit in Inventory</button>;
  }
  const label = `${item.product_name} ${item.quantity} ${item.units} at ${item.outlet_name}`;
  const open = () => {
    setError('');
    setForm({ stock: numberText(item.countInStock ?? 0), selling: numberText(item.dprice),
      mrp: numberText(item.price ?? item.mrp), mfg: dateInput(item.mfg_date), expiry: dateInput(item.exp_date) });
  };
  const set = field => event => setForm(current => ({ ...current, [field]: event.target.value }));

  const save = async (event) => {
    event.preventDefault();
    const stock = Number(form.stock), selling = Number(form.selling), mrp = Number(form.mrp);
    if (form.stock.trim() === '' || !Number.isFinite(stock) || stock < 0) { setError('Enter a stock quantity of zero or more.'); return; }
    const priceChanged = form.selling.trim() !== numberText(item.dprice) || form.mrp.trim() !== numberText(item.price ?? item.mrp);
    if (priceChanged) {
      if (form.selling.trim() === '' || !Number.isFinite(selling) || selling <= 0) { setError('Enter a selling price greater than zero.'); return; }
      if (form.mrp.trim() === '' || !Number.isFinite(mrp) || mrp <= 0) { setError('Enter an MRP greater than zero.'); return; }
      if (selling > mrp) { setError('Selling price cannot be more than MRP.'); return; }
    }
    if (form.mfg && form.expiry && form.expiry < form.mfg) { setError('Expiry date cannot be before MFG date.'); return; }

    // Send only what changed, so an untouched field is never overwritten.
    const changes = {};
    if (stock !== Number(item.countInStock ?? 0)) changes.newQuantity = stock;
    if (priceChanged) { changes.dprice = selling; changes.price = mrp; changes.Discount = Math.floor(Math.max(0, mrp - selling) / mrp * 100); }
    if (form.mfg !== dateInput(item.mfg_date)) changes.mfg_date = form.mfg || null;
    if (form.expiry !== dateInput(item.exp_date)) changes.exp_date = form.expiry || null;
    if (!Object.keys(changes).length) { setForm(null); return; }

    setSaving(true); setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/products/stock/${item.productID}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ brandID: item.brandID, financialID: item.financialID, ...changes, location: item.outlet_name }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || 'Outlet stock update failed');
      setForm(null); onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  const field = 'mt-1 w-full rounded border p-2';
  return <>
    <button type="button" className="text-blue-700 underline" onClick={open}>Edit</button>
    {form && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-label={`Edit ${label}`}>
      <form noValidate onSubmit={save} className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-bold">Edit outlet stock</h3>
        <p className="mt-1 text-sm text-gray-500">{label}</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="text-sm font-medium">Stock
            <input aria-label={`Stock for ${label}`} type="number" min="0" step="any" required value={form.stock} disabled={saving} onChange={set('stock')} className={field} />
          </label>
          <label className="text-sm font-medium">Selling price / unit
            <input aria-label={`Selling price for ${label}`} type="number" min="0" step="any" required value={form.selling} disabled={saving} onChange={set('selling')} className={field} />
          </label>
          <label className="text-sm font-medium">MRP / unit
            <input aria-label={`MRP for ${label}`} type="number" min="0" step="any" required value={form.mrp} disabled={saving} onChange={set('mrp')} className={field} />
          </label>
          <div />
          <label className="text-sm font-medium">MFG date
            <input aria-label={`MFG date for ${label}`} type="date" value={form.mfg} disabled={saving} onChange={set('mfg')} className={field} />
          </label>
          <label className="text-sm font-medium">Expiry date
            <input aria-label={`Expiry date for ${label}`} type="date" value={form.expiry} disabled={saving} onChange={set('expiry')} className={field} />
          </label>
        </div>
        {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" disabled={saving} onClick={() => { setForm(null); setError(''); }} className="rounded border px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="rounded bg-green-700 px-4 py-2 text-white">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>}
  </>;
}
